import { MutableRefObject, useEffect, useRef } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useNavigate } from 'react-router-dom-v5-compat';

import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import {
  AddDataSourceResponse,
  FormNewDataSourceData,
  FormNewDataSourceDataS3,
  pluckAttributesFromFormData,
} from 'dashboard/forms/FormNewDataSource/types';
import { Backdrop } from 'dashboard/layouts/ShowViewLayoutV2';
import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import {
  DataSourceStatus,
  DataSourceUniqueStatus,
  ExternalIntegration,
  ExternalIntegrationDataSource,
  ExternalIntegrationDataSourceGoogleSheets,
  GoogleSheetsFilesResponse,
  S3Object,
} from 'dashboard/models/external-integration';
import { Importer } from 'dashboard/models/importer';
import { setExternalIntegration } from 'dashboard/redux/actions/external-integrations';
import { showMessage } from 'dashboard/redux/actions/messages';
import { RootState } from 'dashboard/redux/store';
import { refreshDataSource } from 'dashboard/redux/thunk-actions/data-source';
import {
  archiveExternalIntegration,
  loadExternalIntegrations,
} from 'dashboard/redux/thunk-actions/external-integrations';
import {
  archiveImporter,
  cloneImporter,
  createImporter,
  loadImportersList,
} from 'dashboard/redux/thunk-actions/importers';
import { loadS3Config } from 'dashboard/redux/thunk-actions/s3';
import DashboardImportersView from 'dashboard/views/DashboardImportersView';
import ApiResponseError from 'lib/errors/ApiResponseError';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import useApiLoader from 'lib/hooks/useApiLoader';
import {
  ApiEndpoint,
  ApiModel,
  ExternalIntegrationDataSource as ApiModelExternalIntegrationDataSource,
} from 'lib/interfaces/api';
import { isPermissionsError, permissionsErrorMessage } from 'lib/permissions-errors';

const DashboardImportsRoute = (props: PropsFromRedux) => {
  const navigate = useNavigate();

  const { loading: isExternalIntegrationsLoading } = useApiLoader({
    loader: async () => {
      await props.loadExternalIntegrations();
    },
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load data sources, please try again', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
    deps: [props.loadExternalIntegrations, props.showMessage],
  });

  const { loading: s3ConfigLoading } = useApiLoader({
    loader: async () => {
      await props.loadS3Config();
    },
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load S3 config, please try again', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
    deps: [props.loadS3Config, props.showMessage],
  });

  const { loading: isImportersLoading } = useApiLoader({
    loader: async () => {
      await props.loadImportersList(props.currentEnvironment);
    },
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load importers, please try again', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
    deps: [props.currentEnvironment, props.loadImportersList, props.showMessage],
  });

  const { error: refreshDataSourceError, callback: handleRefreshDataSource } = useApiCallback(
    async (dataSource: ExternalIntegrationDataSource) => {
      await props.refreshDataSource(dataSource);
      props.showMessage('Refreshed Data source', MessageType.Success);
    },
    [props.refreshDataSource, props.showMessage],
  );

  useApiErrorHandler({
    error: refreshDataSourceError,
    onError: () => props.showMessage('Failed to refresh data source.', MessageType.Failure),
    onNetworkError: () => props.showMessage('Failed to refresh data source.', MessageType.Failure),
  });

  const isDataSource = (e: ExternalIntegration): e is ExternalIntegrationDataSource => {
    return ApiModelExternalIntegrationDataSource.includes(e.type);
  };

  const timeoutInterval = 5000;
  const timer: MutableRefObject<ReturnType<typeof setInterval> | null> = useRef<ReturnType<
    typeof setInterval
  > | null>(null);
  useEffect(() => {
    if (isExternalIntegrationsLoading) return;
    const dataSources: ExternalIntegrationDataSource[] =
      props.externalIntegrations.filter(isDataSource);
    const isDataSourceProcessing = dataSources.some(
      (ds) => (ds.attributes.status as DataSourceStatus) === DataSourceUniqueStatus.Processing,
    );

    if (isDataSourceProcessing) {
      // start polling if not already
      if (!timer.current) {
        timer.current = setInterval(async () => {
          await props.loadExternalIntegrations();
        }, timeoutInterval);
      }
    }
    // stop polling if no longer need to poll
    else if (timer.current) {
      clearInterval(timer.current);
      timer.current = null;
    }

    // clear the timer upon unmount
    return () => {
      if (timer.current) {
        clearInterval(timer.current);
        timer.current = null;
      }
    };
  }, [isExternalIntegrationsLoading, props.externalIntegrations]);

  const addDataSource = async (values: FormNewDataSourceData): Promise<AddDataSourceResponse> => {
    try {
      const attributes = pluckAttributesFromFormData(values);
      const response = await SessionClientDashboard.postResourceCreate<ExternalIntegration>(
        {
          attributes,
          type: values.type,
        },
        { contentType: 'multipart/form-data' },
      );

      props.setExternalIntegration(response.data);
      props.showMessage('Successfully added data source', MessageType.Success);
      return response.data;
    } catch (err) {
      if (err instanceof ApiResponseError && isPermissionsError(err)) {
        props.showMessage(permissionsErrorMessage(err), MessageType.Failure);
      } else if (err instanceof ApiResponseError && err.status === 400) {
        // If inputs are invalid, parse the error message and propogate it back to the form
        if (err.errors && err.errors[0]?.title === 'Bad request' && err.errors[0].details) {
          return err.errors[0].details.toString();
        } else {
          props.showMessage('Bad Request. Unable to Parse Error.', MessageType.Failure);
        }
      } else {
        props.showMessage('Please try again.', MessageType.Failure);
      }
    }
    return null;
  };

  const getAllGoogleSheets = async (
    code: string,
    redirectUrl: string,
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  ): Promise<GoogleSheetsFilesResponse> => {
    try {
      const attributes = { code, redirectUrl };

      const response = await SessionClientDashboard.postResourceSingletonActionNew<
        ExternalIntegrationDataSourceGoogleSheets,
        {
          type: ApiModel.ExternalIntegrationDataSourceGoogleSheets;
          attributes: { code: string; redirectUrl: string };
        }
      >(ApiEndpoint.ExternalIntegrationDataSource, 'schema', {
        attributes,
        type: ApiModel.ExternalIntegrationDataSourceGoogleSheets,
      });
      props.showMessage('Successfully authorized to Google Sheets.', MessageType.Success);

      return {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        token: response.meta.token,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        files: response.meta.files,
      };
    } catch (err) {
      if (err instanceof ApiResponseError && isPermissionsError(err)) {
        props.showMessage(permissionsErrorMessage(err), MessageType.Failure);
      } else {
        props.showMessage('Please try again.', MessageType.Failure);
      }
    }
  };

  const handleListS3Objects = async (values: FormNewDataSourceDataS3) => {
    const res = await SessionClientDashboard.getEndpoint<{ objects: S3Object[] }>(
      ApiEndpoint.DataSourceS3ListObjects,
      {
        filter: {
          arn: values.credentials.arn,
          external_id: values.credentials.externalId,
          region: values.region,
          bucket: values.bucket,
          prefix: values.csvPathPrefix,
        },
      },
    );
    return res.objects;
  };

  const archiveDataSource = async (dataSource: ExternalIntegrationDataSource): Promise<void> => {
    try {
      await props.archiveExternalIntegration(dataSource);
    } catch (err) {
      props.showMessage('Please try again.', MessageType.Failure);
    }
  };

  const archiveImporter = async (importer: Importer): Promise<void> => {
    try {
      await props.archiveImporter(importer);
    } catch (err) {
      props.showMessage(
        'There was an error archiving the importer. Please try again.',
        MessageType.Failure,
      );
      return;
    }

    props.showMessage(
      `Successfully archived ${importer.attributes.name} importer.`,
      MessageType.Success,
    );
  };

  const cloneImporter = async (
    importer: Importer,
    name: string,
    dataSourceId: string,
  ): Promise<void> => {
    try {
      const response = await props.cloneImporter(importer, name, dataSourceId);

      navigate(`/dashboard/imports/importers/${response.data.id}/data-mapping`);
    } catch (err) {
      if (err instanceof ApiResponseError && err.status === 422) {
        props.showMessage(
          'This importer name is already in use. Please try again with a different name.',
          MessageType.Failure,
        );
      } else {
        props.showMessage(
          'There was an error cloning the importer. Please try again.',
          MessageType.Failure,
        );
      }
      return;
    }
  };

  if (isExternalIntegrationsLoading || isImportersLoading || s3ConfigLoading) {
    return (
      <Backdrop>
        <LoadingIndicator fullscreen={true} />
      </Backdrop>
    );
  } else {
    const dataSources = props.externalIntegrations.filter(isDataSource);
    return (
      <DashboardImportersView
        addDataSource={addDataSource}
        getAllGoogleSheets={getAllGoogleSheets}
        archiveImporter={archiveImporter}
        cloneImporter={cloneImporter}
        archiveDataSource={archiveDataSource}
        refreshDataSource={handleRefreshDataSource}
        dataSources={dataSources}
        importers={props.importers}
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        s3Config={props.s3Config}
        handleListS3Objects={handleListS3Objects}
      />
    );
  }
};

const mapStateToProps = (state: RootState) => {
  return {
    currentEnvironment: state.currentEnvironment.currentEnvironment,
    externalIntegrations: state.externalIntegrations.externalIntegrations,
    importers: state.importers.importers,
    s3Config: state.s3.s3Config,
  };
};

const mapDispatchToProps = {
  archiveExternalIntegration,
  archiveImporter,
  cloneImporter,
  createImporter,
  loadExternalIntegrations,
  loadImportersList,
  loadS3Config,
  refreshDataSource,
  setExternalIntegration,
  showMessage,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DashboardImportsRoute);
